/**
 * AssignCustomerModal Component
 * Governed staff assignment for individual taxpayers:
 * - Technical Assistant (Tax Preparer)
 * - CA Partner (Reviewing CA)
 * - Research Assistant (Advisory / Legal Researcher)
 * Includes mandatory audit reason according to the canonical assignment contract.
 */

import { useState, useEffect, useMemo } from 'react'
import { X, Users, UserCheck, ShieldCheck, Search, AlertCircle } from 'lucide-react'
import { useCustomerStore } from '@/store/customerStore'
import { useUserStore } from '@/store/userStore'
import { useAuth } from '@/contexts/AuthContext'
import { resolveRole } from '@/security/accessContract'
import { toast } from '@/store/toastStore'
import type { IndividualCustomer, CustomerAssignmentInput } from '@/types/customer'
import type { User } from '@/types/user.types'

interface AssignCustomerModalProps {
  isOpen: boolean
  onClose: () => void
  customer?: IndividualCustomer | null
  customerIds?: string[]
  onSuccess?: () => void
}

export default function AssignCustomerModal({
  isOpen,
  onClose,
  customer,
  customerIds,
  onSuccess,
}: AssignCustomerModalProps) {
  const { user: currentUser } = useAuth()
  const { assignCustomer, bulkAssignCA } = useCustomerStore()
  const { users, loadUsers } = useUserStore()

  const [assignedToUserId, setAssignedToUserId] = useState<string>('')
  const [reviewerUserId, setReviewerUserId] = useState<string>('')
  const [researcherUserId, setResearcherUserId] = useState<string>('')
  const [reason, setReason] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const isBulk = Boolean(customerIds && customerIds.length > 0 && !customer)
  const targetCount = isBulk ? customerIds!.length : 1

  // Load team users on mount
  useEffect(() => {
    if (isOpen) {
      if (users.length === 0) {
        loadUsers(currentUser?.tenantId ?? '')
      }
      // Populate single customer assignments
      if (customer) {
        setAssignedToUserId(customer.assignedCA || '')
        setReviewerUserId(customer.reviewerUserId || '')
        setResearcherUserId(customer.researcherUserId || '')
      } else {
        setAssignedToUserId('')
        setReviewerUserId('')
        setResearcherUserId('')
      }
      setReason('')
      setSubmitError(null)
    }
  }, [isOpen, customer, currentUser?.tenantId, loadUsers, users.length])

  // Filter and group active staff by canonical role
  const { technicalAssistants, caPartners, researchAssistants } = useMemo(() => {
    const activeUsers = users.filter((u) => u.isActive)

    const tech: User[] = []
    const partners: User[] = []
    const research: User[] = []

    for (const u of activeUsers) {
      const canonical = resolveRole(u.role).canonicalRole
      if (canonical === 'technical_assistant') {
        tech.push(u)
      } else if (canonical === 'ca_partner' || canonical === 'firm_owner' || canonical === 'firm_admin') {
        partners.push(u)
      } else if (canonical === 'research_assistant') {
        research.push(u)
      }
    }

    return {
      technicalAssistants: tech,
      caPartners: partners,
      researchAssistants: research,
    }
  }, [users])

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitError(null)

    if (!reason.trim() || reason.trim().length < 3) {
      setSubmitError('A reason of at least 3 characters is required for audit governance.')
      return
    }

    if (!assignedToUserId && !reviewerUserId && !researcherUserId) {
      setSubmitError('Please select at least one staff member to assign.')
      return
    }

    setIsSubmitting(true)

    try {
      const assignedToUser = users.find((u) => u.userId === assignedToUserId)
      const reviewerUser = users.find((u) => u.userId === reviewerUserId)
      const researcherUser = users.find((u) => u.userId === researcherUserId)

      const payload: CustomerAssignmentInput = {
        ...(assignedToUserId && {
          assignedToUserId,
          assignedToName: assignedToUser?.fullName || undefined,
        }),
        ...(reviewerUserId && {
          reviewerUserId,
          reviewerName: reviewerUser?.fullName || undefined,
        }),
        ...(researcherUserId && {
          researcherUserId,
          researcherName: researcherUser?.fullName || undefined,
        }),
        reason: reason.trim(),
      }

      if (isBulk && customerIds) {
        if (assignedToUserId) {
          await bulkAssignCA(customerIds, assignedToUserId)
        }
        // Note: For comprehensive governance, each customer in bulk receives individual assignment if needed
        for (const cid of customerIds) {
          try {
            await assignCustomer(cid, payload)
          } catch {
            // best-effort per customer in bulk
          }
        }
        toast.success(
          `${customerIds.length} customers assigned successfully`,
          `Assigned to selected team members with audit reason.`
        )
      } else if (customer) {
        await assignCustomer(customer.id, payload)
        toast.success(
          `Assignment updated for ${customer.fullName}`,
          `Team roles updated with audit log.`
        )
      }

      onSuccess?.()
      onClose()
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to update customer assignment.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 z-[9999] animate-in fade-in duration-200">
      <div className="bg-white dark:bg-surface-dark rounded-xl border border-surface-overlay shadow-2xl max-w-lg w-full p-6 space-y-5 animate-in zoom-in-95 slide-in-from-bottom-2 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-surface-overlay pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400">
              <UserCheck className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-text-primary">
                {isBulk ? `Assign Team (${targetCount} Customers)` : `Assign Team for ${customer?.fullName}`}
              </h2>
              <p className="text-xs text-text-secondary">
                Assign Technical Assistant, Reviewing Partner, or Research Assistant
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-circle btn-sm text-text-secondary hover:text-text-primary"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {submitError && (
          <div className="p-3 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-sm flex items-start gap-2">
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <span>{submitError}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* 1. Technical Assistant / Primary Preparer */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              <span className="flex items-center gap-2">
                <Users className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                Technical Assistant / Tax Preparer
              </span>
            </label>
            <select
              value={assignedToUserId}
              onChange={(e) => setAssignedToUserId(e.target.value)}
              className="input input-bordered w-full text-sm bg-surface-light dark:bg-surface-elevated text-text-primary"
            >
              <option value="">None / Unassigned</option>
              {technicalAssistants.length > 0 ? (
                technicalAssistants.map((u) => (
                  <option key={u.userId} value={u.userId}>
                    {u.fullName} ({u.email})
                  </option>
                ))
              ) : (
                <option disabled value="__none__">No Technical Assistants available</option>
              )}
              {/* Also allow assigning other staff if needed */}
              {caPartners.length > 0 && (
                <optgroup label="CA Partners (Sole Preparer)">
                  {caPartners.map((u) => (
                    <option key={u.userId} value={u.userId}>
                      {u.fullName} ({u.email})
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            <p className="text-xs text-text-tertiary mt-1">
              Responsible for document verification, income computation, and draft schedule preparation.
            </p>
          </div>

          {/* 2. Reviewing CA Partner */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              <span className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                Reviewing CA Partner (Signing Authority)
              </span>
            </label>
            <select
              value={reviewerUserId}
              onChange={(e) => setReviewerUserId(e.target.value)}
              className="input input-bordered w-full text-sm bg-surface-light dark:bg-surface-elevated text-text-primary"
            >
              <option value="">None / Unassigned</option>
              {caPartners.map((u) => (
                <option key={u.userId} value={u.userId}>
                  {u.fullName} ({u.role.replace(/_/g, ' ')}) - {u.email}
                </option>
              ))}
            </select>
            <p className="text-xs text-text-tertiary mt-1">
              Responsible for professional review, quality sign-off, and final e-filing authorization.
            </p>
          </div>

          {/* 3. Research Assistant */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              <span className="flex items-center gap-2">
                <Search className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                Research Assistant (Advisory / Notices)
              </span>
            </label>
            <select
              value={researcherUserId}
              onChange={(e) => setResearcherUserId(e.target.value)}
              className="input input-bordered w-full text-sm bg-surface-light dark:bg-surface-elevated text-text-primary"
            >
              <option value="">None / Unassigned</option>
              {researchAssistants.length > 0 ? (
                researchAssistants.map((u) => (
                  <option key={u.userId} value={u.userId}>
                    {u.fullName} ({u.email})
                  </option>
                ))
              ) : (
                <option disabled value="__none__">No Research Assistants available</option>
              )}
            </select>
            <p className="text-xs text-text-tertiary mt-1">
              Conducts case-law research, prepares advisory memorandums, and drafts responses to tax notices.
            </p>
          </div>

          {/* 4. Mandatory Reason Field */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              <span className="flex items-center gap-1">
                Reason for Assignment <span className="text-red-500">*</span>
              </span>
            </label>
            <textarea
              required
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Assigned for FY 2024-25 ITR filing and compliance review"
              className="input input-bordered w-full h-20 text-sm py-2 bg-surface-light dark:bg-surface-elevated text-text-primary"
            />
            <p className="text-xs text-text-tertiary mt-1">
              Mandatory audit requirement. This reason is permanently recorded in the customer assignment history.
            </p>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-surface-overlay">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="btn btn-ghost btn-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-primary btn-sm flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <span className="loading loading-spinner loading-xs" />
                  <span>Saving…</span>
                </>
              ) : (
                <>
                  <UserCheck className="w-4 h-4" />
                  <span>Save Assignment</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
